import {
  createCustomerServiceItem,
  createCustomerServiceItemVariables,
  exportCustomerServiceItems,
  exportCustomerServiceItemsVariables,
  pendingCustomerServiceItems,
  pendingCustomerServiceItemsVariables,
  type customerServiceItems,
  type customerServiceItemsVariables,
  type manageCustomerServiceItems,
  type manageCustomerServiceItemsVariables,
} from "#types/code-generator.ts";
import type { TypedDocumentNode } from "#types/graphql.d.ts";
import { gql } from "graphql-tag";

const CustomerServiceItemPayloadFragment = gql`
  fragment CustomerServiceItemPayload on CustomerServiceItem {
    id
    createdAt
    evaluation {
      id
      createdAt
      program {
        id
        title
      }
      recommendedProgram {
        id
        title
      }
    }
    recurringCharge {
      id
      amount
      billingCategory {
        id
        title
      }
      finalDate
    }
    recurringCredit {
      id
      amount
      billingCategory {
        id
        title
      }
      finalDate
    }
    type
    unread
    user {
      id
      balance
      firstName
      lastName
      phone
      email
      invoices {
        id
        status
        invoiceItems {
          id
          amount
          coupons {
            id
            amount
          }
          createdAt
          credits {
            id
            amount
          }
          discounts {
            id
            amount
          }
          payments {
            id
            amount
            payment {
              id
              description
              type
            }
          }
          refunds {
            id
            amount
          }
        }
      }
      stripeToken
      adyenToken
    }
  }
`;

export const CreateCustomerServiceItemMutation = gql`
  mutation createCustomerServiceItem($data: CustomerServiceItemCreateInput!) {
    createCustomerServiceItem(data: $data) {
      ...CustomerServiceItemPayload
    }
  }
  ${CustomerServiceItemPayloadFragment}
` as TypedDocumentNode<
  createCustomerServiceItem,
  createCustomerServiceItemVariables
>;

export const UpdateCustomerServiceItemMutation = gql`
  mutation updateCustomerServiceItem(
    $data: CustomerServiceItemCreateInput!
    $where: UniqueInput!
  ) {
    updateCustomerServiceItem(data: $data, where: $where) {
      ...CustomerServiceItemPayload
    }
  }
  ${CustomerServiceItemPayloadFragment}
`;

export const DeleteCustomerServiceItemMutation = gql`
  mutation deleteCustomerServiceItem($id: String!) {
    deleteCustomerServiceItem(where: { id: $id }) {
      id
    }
  }
`;

export const CustomerServiceItemsQuery = gql`
  query customerServiceItems($organizationId: String!) {
    customerServiceItems(
      where: { organizationId: $organizationId, unread: true }
      orderBy: { createdAt: asc }
    ) {
      ...CustomerServiceItemPayload
    }
  }
  ${CustomerServiceItemPayloadFragment}
` as TypedDocumentNode<customerServiceItems, customerServiceItemsVariables>;

export const ExportCustomerServiceItemsMutation = gql`
  mutation exportCustomerServiceItems($where: CustomerServiceItemWhereInput) {
    exportCustomerServiceItems(where: $where)
  }
` as TypedDocumentNode<
  exportCustomerServiceItems,
  exportCustomerServiceItemsVariables
>;

export const PendingCustomerServiceItemsQuery = gql`
  query pendingCustomerServiceItems($where: CustomerServiceItemWhereInput!) {
    customerServiceItems(where: $where) {
      id
      createdAt
      additionalData
      type
      evaluation {
        id
        createdAt
        instructor {
          firstName
          lastName
        }
        program {
          id
          title
        }
        recommendedProgram {
          id
          title
        }
      }
      integration
      membershipOffering {
        id
      }
      organization {
        id
        title
        subdomain
      }
      payment {
        id
        amount
        createdAt
        stripeChargeId
      }
      recurringCharge {
        id
        amount
        description
        finalDate
      }
      recurringCredit {
        id
        amount
        description
        finalDate
      }
      user {
        id
        accountOwner {
          id
          firstName
          lastName
          email
        }
        avatar {
          url
        }
        birthDate
        firstName
        gender
        lastName
        level
        phone
        email
        usaSwimmingMemberId
        studentGroupRegistrations(
          where: { status: { in: [Approved, Pending] } }
        ) {
          id
          class {
            id
            program {
              id
              title
            }
          }
        }
      }
      value
    }
  }
` as TypedDocumentNode<
  pendingCustomerServiceItems,
  pendingCustomerServiceItemsVariables
>;

export const ManageCustomerServiceItemsQuery = gql`
  query manageCustomerServiceItems($where: CustomerServiceItemWhereInput!) {
    customerServiceItems(where: $where) {
      id
      type
      user {
        id
        balance
      }
    }
  }
` as TypedDocumentNode<
  manageCustomerServiceItems,
  manageCustomerServiceItemsVariables
>;
